// Which room each route lives in, and the shots the camera frames them with.
import type { Rect, Shot, Viewport } from './camera';
import { clampCenter, fitScale, PHONE_FOCUS_W, ROOM_FOCUS } from './camera';
import type { FloorId, WorldMeta } from './assets';
import type { Scene } from './scene';

/** Room for a pathname (spec section 2). The argument route moves between rooms by phase. */
export function roomForPath(path: string): string {
  if (path === '/' ) return 'front';
  if (path.startsWith('/home')) return 'clerk';
  if (path.startsWith('/case/')) return 'lounge';
  if (/^\/play\/[^/]+\/summary/.test(path)) return 'pio';
  if (path.startsWith('/play/')) return 'courtroom';
  if (path.startsWith('/g/')) return 'pio';
  if (path.startsWith('/record')) return 'library';
  if (path.startsWith('/guide')) return 'library';
  if (path.startsWith('/achievements')) return 'clerk';
  if (path.startsWith('/leaderboard')) return 'hoops';
  if (path.startsWith('/settings')) return 'robing';
  if (path.startsWith('/about')) return 'greathall';
  if (path.startsWith('/court')) return 'chambers';
  if (path.startsWith('/terms')) return 'pio';
  return 'police';
}

/** Where a map plaque takes you; rooms without a screen of their own are not buttons. */
export const ROOM_ROUTES: Record<string, { to: string; holds: string }> = {
  front: { to: '/', holds: 'the way in' },
  clerk: { to: '/home', holds: 'the docket' },
  lounge: { to: '/home', holds: 'the briefs, once you take a case' },
  courtroom: { to: '/home', holds: 'argument, once you take a case' },
  greathall: { to: '/about', holds: 'about the simulation' },
  chambers: { to: '/court', holds: 'the justices' },
  robing: { to: '/settings', holds: 'your settings' },
  library: { to: '/record', holds: 'your record and the guide' },
  hoops: { to: '/leaderboard', holds: 'the leaderboard' },
  pio: { to: '/terms', holds: 'opinions and notices' },
  police: { to: '/not-found', holds: 'lost and found' },
};

export function isPhone(vp: Viewport): boolean { return vp.w <= 768; }

function floorBounds(m: WorldMeta, floor: FloorId): Rect {
  const f = m.floors[floor];
  return [f.at[0], f.at[1], f.w, f.h];
}

export function roomShot(m: WorldMeta, scene: Scene, vp: Viewport): Shot | null {
  const room = m.rooms[scene.room];
  if (!room) return null;
  const lit = scene.lit === undefined ? (room.lit ?? room.rect) : scene.lit;
  const r = room.rect;
  const focusW = scene.focus ? scene.focus[0] : isPhone(vp) ? PHONE_FOCUS_W : ROOM_FOCUS.w;
  const focusH = scene.focus ? scene.focus[1] : isPhone(vp) ? Math.max(1, (vp.h / vp.w) * PHONE_FOCUS_W * 0.5) : ROOM_FOCUS.h;
  const scale = fitScale(focusW, focusH, vp);
  const [cx0, cy0] = scene.center ?? [r[0] + r[2] / 2, r[1] + r[3] / 2];
  const [bx, by, bw, bh] = floorBounds(m, room.floor);
  const [cx, cy] = clampCenter(cx0 - bx, cy0 - by, scale, vp, bw, bh);
  return { floor: room.floor, cx: cx + bx, cy: cy + by, scale, lit, dim: scene.dim === false ? 0 : 1 };
}

/** Which floor a room is on, known before world.json loads. */
export function floorOfRoom(room: string | null | undefined): FloorId {
  if (!room) return 'f1';
  if (['clerk', 'pio', 'police', 'giftshop', 'cafe', 'lowerhall'].includes(room)) return 'g';
  if (room === 'library') return 'l3';
  if (room === 'hoops') return 'h5';
  if (room === 'front') return 'ext';
  return 'f1';
}

/** Room names for the HUD plaque (the same names world.json carries, available before it loads). */
export const ROOM_LABELS: Record<string, string> = {
  courtroom: 'Courtroom', greathall: 'Great Hall', robing: 'Robing Room', conference: 'Conference Room',
  chambers: 'Chambers', chief: "The Chief's Chambers", marshal: "Marshal's Office", lounge: "Lawyers' Lounge",
  plaza: 'Front Plaza', clerk: "Clerk's Office", pio: 'Public Information Office', police: 'Court Police',
  giftshop: 'Gift Shop', cafe: 'Cafeteria', lowerhall: 'Lower Great Hall', library: 'Library',
  hoops: 'Basketball Court', front: 'West Front',
};
